import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { articles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'bianma-biaozhun');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const currentCategory = categoryMap[activeSlug];
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug],
  );

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-foreground-900 mb-3">分类未找到</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory.name },
  ];

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 高清无码视频画质指南`,
    description: currentCategory.description,
    url: `https://hd-uncensored-guide.readdy.site/category/${activeSlug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>

      <div className="pt-20 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className={`${currentCategory.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">{currentCategory.name}</h1>
            </div>
            <p className="text-sm text-foreground-600 ml-13">{currentCategory.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-file-list-3-line text-4xl text-foreground-300 mb-3 block"></i>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}